import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { familyCodeService } from './familyCodeService';
import { subscriptionService } from './subscriptionService';
import logger from '../utils/logger';

/**
 * Reciprocal relationship mapping
 * Maps a relationship to its reciprocal for automatic Family Tree linking
 */
const RECIPROCAL_RELATIONSHIPS = {
  // Immediate Family
  spouse: 'spouse',
  partner: 'partner',
  child: 'parent',
  parent: 'child',
  sibling: 'sibling',

  // Extended Family
  'sibling-child': 'aunt_uncle', // Their sibling's child sees them as aunt/uncle
  grandparent: 'grandchild',
  grandchild: 'grandparent',
  'aunt/uncle': 'niece_nephew',
  'niece/nephew': 'aunt_uncle',
  cousin: 'cousin',

  // Non-family
  friend: 'friend',
  caregiver: 'caregiver',
  'power-of-attorney': 'power-of-attorney',
  executor: 'executor',
  trustee: 'trustee',
  advisor: 'advisor',
  attorney: 'attorney',
  accountant: 'accountant',
  'financial-planner': 'financial-planner',
  doctor: 'doctor',
  'emergency-contact': 'emergency-contact',
  other: 'other'
};

/**
 * Get the reciprocal relationship type
 * @param {string} relationship - Original relationship type
 * @returns {string} Reciprocal relationship type
 */
function getReciprocalRelationship(relationship) {
  return RECIPROCAL_RELATIONSHIPS[relationship] || 'other';
}

/**
 * Relationship to Groups mapping
 * Auto-assigns groups based on relationship type.
 * Groups match GROUP_TYPES in defaultGroups.js.
 *
 * Professional/external relationships map to restricted groups (ADVISORS,
 * LEGAL_FINANCIAL, CAREGIVERS, EMERGENCY_CONTACTS) that block access to
 * family-wide collections at the Firestore rules level.
 */
const RELATIONSHIP_GROUP_MAPPING = {
  // Immediate Family (full access)
  spouse: ['IMMEDIATE_FAMILY'],
  partner: ['IMMEDIATE_FAMILY'],
  child: ['IMMEDIATE_FAMILY'],
  parent: ['PARENTS', 'IMMEDIATE_FAMILY'],
  sibling: ['IMMEDIATE_FAMILY'],

  // Extended Family with specific groups
  'sibling-child': ['SIBLING_CHILDREN'],
  grandparent: ['GRANDPARENTS'],
  grandchild: ['EXTENDED_FAMILY'],
  'aunt/uncle': ['PARENTS_SIBLINGS'],
  'niece/nephew': ['SIBLING_CHILDREN'],
  cousin: ['EXTENDED_FAMILY'],

  // Close social connections
  friend: ['FRIENDS_OF_FAMILY'],

  // Caregivers — health + messaging, no vault
  caregiver: ['CAREGIVERS'],
  doctor: ['CAREGIVERS'],

  // Legal/financial professionals — messaging + explicitly shared docs only
  'power-of-attorney': ['LEGAL_FINANCIAL'],
  executor: ['LEGAL_FINANCIAL'],
  trustee: ['LEGAL_FINANCIAL'],
  attorney: ['LEGAL_FINANCIAL'],
  accountant: ['LEGAL_FINANCIAL'],
  'financial-planner': ['LEGAL_FINANCIAL'],

  // General professional advisors — messaging + explicitly shared docs only
  advisor: ['ADVISORS'],

  // Emergency contacts — messaging only
  'emergency-contact': ['EMERGENCY_CONTACTS'],

  other: ['EXTENDED_FAMILY']
};

/**
 * Service for managing smart family invitations
 * Invitations stored at: families/{familyCode}/invitations/{inviteId}/
 */
class InvitationService {
  /**
   * Create a new family invitation
   * @param {string} familyCode - Family code
   * @param {string} inviterId - User ID sending the invitation
   * @param {Object} invitationData - Invitation details
   * @returns {Promise<Object>} Created invitation with code
   */
  async createInvitation(familyCode, inviterId, invitationData) {
    if (!familyCode || !inviterId) {
      throw new Error('Family code and inviter ID are required');
    }

    // Validate inviter is a member of the family
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', inviterId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      throw new Error('Inviter is not a member of this family');
    }

    // Generate unique invitation code
    const inviteCode = nanoid(12).toUpperCase();
    const invitationsCollection = collection(familyRef, 'invitations');
    const inviteRef = doc(invitationsCollection, inviteCode);

    // Auto-assign groups based on relationship
    const relationship = invitationData.relationship || 'other';
    const autoAssignGroups = RELATIONSHIP_GROUP_MAPPING[relationship] || ['EXTENDED_FAMILY'];

    // Create invitation document
    const invitation = {
      inviteCode,
      familyCode,
      inviterId,
      inviterName: memberDoc.data().displayName || 'Family Member',
      inviterEmail: memberDoc.data().email,

      // Invitee details
      inviteeEmail: invitationData.inviteeEmail || null,
      inviteePhone: invitationData.inviteePhone || null,
      inviteeName: invitationData.inviteeName || null,

      // Relationship and permissions
      relationship: relationship,
      autoAssignGroups: autoAssignGroups,
      customMessage: invitationData.customMessage || '',

      // Status tracking
      status: 'pending', // pending, accepted, declined, expired
      sentAt: new Date(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
      acceptedAt: null,
      acceptedBy: null,

      // Sponsor seat — inviter covers this member under their plan seat
      sponsorSeat: invitationData.sponsorSeat === true,

      // Metadata
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(inviteRef, invitation);

    // Generate invitation link with familyCode and inviteCode
    const inviteLink = `${window.location.origin}/join/${familyCode}/${inviteCode}`;

    return {
      ...invitation,
      inviteLink
    };
  }

  /**
   * Get an invitation by code
   * @param {string} inviteCode - Invitation code
   * @param {string} [familyCode] - Optional family code for direct access (faster, no auth required)
   * @returns {Promise<Object>} Invitation data
   */
  async getInvitation(inviteCode, familyCode = null) {
    if (!inviteCode) {
      throw new Error('Invitation code is required');
    }

    // If familyCode provided, use direct access (no authentication required)
    if (familyCode) {
      const familyRef = doc(db, 'families', familyCode);
      const inviteRef = doc(familyRef, 'invitations', inviteCode);
      const inviteDoc = await getDoc(inviteRef);

      if (!inviteDoc.exists()) {
        throw new Error('Invitation not found');
      }

      const invitationData = inviteDoc.data();

      // Check if expired
      const now = new Date();
      const expiresAt = invitationData.expiresAt.toDate ? invitationData.expiresAt.toDate() : invitationData.expiresAt;

      if (now > expiresAt && invitationData.status === 'pending') {
        // Auto-expire
        await updateDoc(inviteRef, {
          status: 'expired',
          updatedAt: new Date()
        });
        throw new Error('Invitation has expired');
      }

      return {
        id: inviteDoc.id,
        ...invitationData,
        familyId: familyCode
      };
    }

    // Fallback: Search all families (requires authentication)
    const familiesSnapshot = await getDocs(collection(db, 'families'));

    for (const familyDoc of familiesSnapshot.docs) {
      const inviteRef = doc(familyDoc.ref, 'invitations', inviteCode);
      const inviteDoc = await getDoc(inviteRef);

      if (inviteDoc.exists()) {
        const invitationData = inviteDoc.data();

        // Check if expired
        const now = new Date();
        const expiresAt = invitationData.expiresAt.toDate ? invitationData.expiresAt.toDate() : invitationData.expiresAt;

        if (now > expiresAt && invitationData.status === 'pending') {
          // Auto-expire
          await updateDoc(inviteRef, {
            status: 'expired',
            updatedAt: new Date()
          });
          throw new Error('Invitation has expired');
        }

        return {
          id: inviteDoc.id,
          ...invitationData,
          familyId: familyDoc.id
        };
      }
    }

    throw new Error('Invitation not found');
  }

  /**
   * Create bidirectional Family Tree relationships between inviter and invitee
   * @param {string} inviterId - User ID who sent the invitation
   * @param {string} inviteeId - User ID who accepted the invitation
   * @param {string} relationship - Relationship type from inviter's perspective
   * @returns {Promise<void>}
   */
  async createFamilyTreeRelationships(inviterId, inviteeId, relationship) {
    try {
      // Get both users' data
      const inviterRef = doc(db, 'users', inviterId);
      const inviteeRef = doc(db, 'users', inviteeId);

      const [inviterDoc, inviteeDoc] = await Promise.all([
        getDoc(inviterRef),
        getDoc(inviteeRef)
      ]);

      if (!inviterDoc.exists() || !inviteeDoc.exists()) {
        logger.error('One or both users not found for Family Tree relationship creation');
        return;
      }

      const inviterData = inviterDoc.data();
      const inviteeData = inviteeDoc.data();

      // Get existing relationships or initialize empty objects
      const inviterRelationships = inviterData.relationships || {};
      const inviteeRelationships = inviteeData.relationships || {};

      // Calculate reciprocal relationship
      const reciprocalRelationship = getReciprocalRelationship(relationship);

      // Add relationship to inviter's tree (invitee as the specified relationship)
      inviterRelationships[inviteeId] = {
        type: relationship,
        name: inviteeData.displayName || inviteeData.email || 'Unknown',
        addedAt: new Date().toISOString(),
        autoCreated: true // Flag to indicate this was auto-created
      };

      // Add reciprocal relationship to invitee's tree (inviter as the reciprocal)
      inviteeRelationships[inviterId] = {
        type: reciprocalRelationship,
        name: inviterData.displayName || inviterData.email || 'Unknown',
        addedAt: new Date().toISOString(),
        autoCreated: true
      };

      // Update both users' relationships in parallel
      await Promise.all([
        updateDoc(inviterRef, {
          relationships: inviterRelationships,
          updatedAt: serverTimestamp()
        }),
        updateDoc(inviteeRef, {
          relationships: inviteeRelationships,
          updatedAt: serverTimestamp()
        })
      ]);

    } catch (error) {
      // Don't throw error - log it but continue with invitation acceptance
      logger.error('Error creating Family Tree relationships:', error);
    }
  }

  /**
   * Accept an invitation and join the family
   * @param {string} inviteCode - Invitation code
   * @param {string} userId - User ID accepting the invitation
   * @param {Object} userData - User data for profile creation
   * @param {string} [familyCode] - Optional family code for direct lookup (avoids full-scan fallback)
   * @returns {Promise<Object>} Family and member data
   */
  async acceptInvitation(inviteCode, userId, userData, familyCode = null) {
    if (!inviteCode || !userId) {
      throw new Error('Invitation code and user ID are required');
    }

    // Get invitation — pass familyCode to avoid the expensive full-families scan
    const invitation = await this.getInvitation(inviteCode, familyCode);

    if (invitation.status !== 'pending') {
      throw new Error(`Invitation is ${invitation.status}`);
    }

    const resolvedFamilyCode = invitation.familyCode;

    // Join the family using familyCodeService
    await familyCodeService.joinFamily(userId, resolvedFamilyCode, {
      relationship: invitation.relationship,
      autoAssignGroups: invitation.autoAssignGroups,
      invitedBy: invitation.inviterId
    });

    // Automatically create Family Tree relationships (bidirectional)
    await this.createFamilyTreeRelationships(
      invitation.inviterId,
      userId,
      invitation.relationship
    );

    // Update invitation status
    const familyRef = doc(db, 'families', resolvedFamilyCode);
    const inviteRef = doc(familyRef, 'invitations', inviteCode);

    await updateDoc(inviteRef, {
      status: 'accepted',
      acceptedBy: userId,
      acceptedAt: new Date(),
      updatedAt: new Date()
    });

    // Claim a sponsor seat if the inviter covered this member under their plan
    if (invitation.sponsorSeat) {
      try {
        await subscriptionService.claimSeat(resolvedFamilyCode, userId);
      } catch (seatErr) {
        logger.error('acceptInvitation: claimSeat failed (non-fatal):', seatErr);
      }
    }

    return {
      familyCode: resolvedFamilyCode,
      relationship: invitation.relationship,
      groups: invitation.autoAssignGroups,
      invitedBy: invitation.inviterId,
      sponsorSeat: invitation.sponsorSeat || false,
    };
  }

  /**
   * Decline an invitation
   * @param {string} inviteCode - Invitation code
   * @param {string} userId - User ID declining (optional)
   * @returns {Promise<void>}
   */
  async declineInvitation(inviteCode, userId = null) {
    if (!inviteCode) {
      throw new Error('Invitation code is required');
    }

    const invitation = await this.getInvitation(inviteCode);
    const familyRef = doc(db, 'families', invitation.familyCode);
    const inviteRef = doc(familyRef, 'invitations', inviteCode);

    await updateDoc(inviteRef, {
      status: 'declined',
      declinedBy: userId,
      declinedAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Cancel/revoke an invitation
   * @param {string} familyCode - Family code
   * @param {string} inviteCode - Invitation code
   * @param {string} revokerId - User ID revoking (must be inviter or admin)
   * @returns {Promise<void>}
   */
  async revokeInvitation(familyCode, inviteCode, revokerId) {
    if (!familyCode || !inviteCode || !revokerId) {
      throw new Error('Family code, invitation code, and revoker ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const inviteRef = doc(familyRef, 'invitations', inviteCode);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = inviteDoc.data();

    // Check permissions: must be inviter or admin
    const memberRef = doc(familyRef, 'members', revokerId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      throw new Error('Revoker is not a member of this family');
    }

    const memberGroups = memberDoc.data().groups || [];
    const isAdmin = memberGroups.includes('ADMIN');
    const isInviter = invitation.inviterId === revokerId;

    if (!isAdmin && !isInviter) {
      throw new Error('Only the inviter or admins can revoke invitations');
    }

    await updateDoc(inviteRef, {
      status: 'revoked',
      revokedBy: revokerId,
      revokedAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Get all invitations for a family
   * @param {string} familyCode - Family code
   * @param {Object} filters - Optional filters (status, inviterId)
   * @returns {Promise<Array>} Array of invitations
   */
  async getFamilyInvitations(familyCode, filters = {}) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const invitationsCollection = collection(familyRef, 'invitations');

    let invitationsQuery = invitationsCollection;

    // Apply status filter if provided
    if (filters.status) {
      invitationsQuery = query(invitationsCollection, where('status', '==', filters.status));
    }

    const snapshot = await getDocs(invitationsQuery);
    const invitations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Apply inviterId filter in memory if provided
    if (filters.inviterId) {
      return invitations.filter(inv => inv.inviterId === filters.inviterId);
    }

    return invitations;
  }

  /**
   * Get pending invitations sent by a specific user
   * @param {string} familyCode - Family code
   * @param {string} inviterId - Inviter's user ID
   * @returns {Promise<Array>} Array of pending invitations
   */
  async getUserInvitations(familyCode, inviterId) {
    if (!familyCode || !inviterId) {
      throw new Error('Family code and inviter ID are required');
    }

    return this.getFamilyInvitations(familyCode, {
      status: 'pending',
      inviterId: inviterId
    });
  }

  /**
   * Resend an invitation (updates expiration date)
   * @param {string} familyCode - Family code
   * @param {string} inviteCode - Invitation code
   * @param {string} resenderId - User ID resending
   * @returns {Promise<Object>} Updated invitation
   */
  async resendInvitation(familyCode, inviteCode, resenderId) {
    if (!familyCode || !inviteCode || !resenderId) {
      throw new Error('Family code, invitation code, and resender ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const inviteRef = doc(familyRef, 'invitations', inviteCode);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = inviteDoc.data();

    // Can only resend pending or expired invitations
    if (!['pending', 'expired'].includes(invitation.status)) {
      throw new Error('Can only resend pending or expired invitations');
    }

    // Check permissions
    const memberRef = doc(familyRef, 'members', resenderId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      throw new Error('Resender is not a member of this family');
    }

    const memberGroups = memberDoc.data().groups || [];
    const isAdmin = memberGroups.includes('ADMIN');
    const isInviter = invitation.inviterId === resenderId;

    if (!isAdmin && !isInviter) {
      throw new Error('Only the inviter or admins can resend invitations');
    }

    // Update invitation
    const updates = {
      status: 'pending',
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      resentAt: new Date(),
      resentBy: resenderId,
      updatedAt: new Date()
    };

    await updateDoc(inviteRef, updates);

    return {
      id: inviteCode,
      ...invitation,
      ...updates
    };
  }

  /**
   * Delete an invitation
   * @param {string} familyCode - Family code
   * @param {string} inviteCode - Invitation code
   * @returns {Promise<void>}
   */
  async deleteInvitation(familyCode, inviteCode) {
    if (!familyCode || !inviteCode) {
      throw new Error('Family code and invitation code are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const inviteRef = doc(familyRef, 'invitations', inviteCode);

    await deleteDoc(inviteRef);
  }

  /**
   * Bulk create invitations (for importing contacts)
   * @param {string} familyCode - Family code
   * @param {string} inviterId - User ID sending invitations
   * @param {Array<Object>} invitations - Array of invitation data objects
   * @returns {Promise<Array>} Array of created invitations
   */
  async bulkCreateInvitations(familyCode, inviterId, invitations) {
    if (!familyCode || !inviterId || !invitations || invitations.length === 0) {
      throw new Error('Family code, inviter ID, and invitations array are required');
    }

    const createdInvitations = [];

    for (const invitationData of invitations) {
      try {
        const invitation = await this.createInvitation(familyCode, inviterId, invitationData);
        createdInvitations.push(invitation);
      } catch (error) {
        logger.error('Error creating invitation:', error);
        createdInvitations.push({
          error: error.message,
          data: invitationData
        });
      }
    }

    return createdInvitations;
  }

  /**
   * Get invitation statistics for a family
   * @param {string} familyCode - Family code
   * @returns {Promise<Object>} Invitation statistics
   */
  async getInvitationStats(familyCode) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const invitations = await this.getFamilyInvitations(familyCode);

    const stats = {
      total: invitations.length,
      pending: 0,
      accepted: 0,
      declined: 0,
      expired: 0,
      revoked: 0,
      byRelationship: {},
      recentActivity: []
    };

    const now = new Date();

    for (const invitation of invitations) {
      // Count by status
      stats[invitation.status]++;

      // Count by relationship
      const rel = invitation.relationship || 'other';
      stats.byRelationship[rel] = (stats.byRelationship[rel] || 0) + 1;

      // Recent activity (last 7 days)
      const createdAt = invitation.createdAt.toDate ? invitation.createdAt.toDate() : invitation.createdAt;
      const daysSinceCreated = (now - createdAt) / (1000 * 60 * 60 * 24);

      if (daysSinceCreated <= 7) {
        stats.recentActivity.push({
          inviteCode: invitation.inviteCode,
          inviteeName: invitation.inviteeName || invitation.inviteeEmail,
          status: invitation.status,
          createdAt: createdAt
        });
      }
    }

    // Sort recent activity by date (newest first)
    stats.recentActivity.sort((a, b) => b.createdAt - a.createdAt);

    return stats;
  }

  /**
   * Auto-expire old invitations (run periodically)
   * @param {string} familyCode - Family code
   * @returns {Promise<number>} Number of expired invitations
   */
  async expireOldInvitations(familyCode) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const pendingInvitations = await this.getFamilyInvitations(familyCode, { status: 'pending' });
    const now = new Date();
    let expiredCount = 0;

    for (const invitation of pendingInvitations) {
      const expiresAt = invitation.expiresAt.toDate ? invitation.expiresAt.toDate() : invitation.expiresAt;

      if (now > expiresAt) {
        const familyRef = doc(db, 'families', familyCode);
        const inviteRef = doc(familyRef, 'invitations', invitation.inviteCode);

        await updateDoc(inviteRef, {
          status: 'expired',
          updatedAt: new Date()
        });

        expiredCount++;
      }
    }

    return expiredCount;
  }
}

export const invitationService = new InvitationService();
